import React, { useCallback, useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { CalendarClock, EyeOff, Tv, X } from "lucide-react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";

import { TIME_FORMAT_SHORT } from "@/src/constants/dateFormat";

import EncoderAPI from "../../api/EncoderAPI";
import useLiveBannerSnooze from "../../hooks/useLiveBannerSnooze";
import { LIVE_CONTENTS_ROUTE } from "../routes/routes";
import { Button } from "../ui/button";
import { ButtonGroup } from "../ui/button-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

import type { ReactNode } from "react";

import type { LiveStats } from "@/src/api/EncoderAPI.types";
import type { AppState } from "@/src/reducers";

const LIVE_STATUSES = ["activating", "ongoing", "live"];

interface FormattedLiveItem {
  mediaUuid: string;
  label: string;
  timingLabel: string;
}

type FormattedLive =
  | {
      type: "ongoing" | "scheduled";
      mediaUuid: string;
      label: string;
      projectLabel: string;
      title: ReactNode;
      timingLabel: string;
    }
  | {
      type: "multiple-ongoing";
      lives: FormattedLiveItem[];
      title: ReactNode;
    };

interface SingleLiveContentProps {
  live: Extract<FormattedLive, { type: "ongoing" | "scheduled" }>;
  onSnoozeUntilTomorrow: () => void;
  onDismissForever: () => void;
}

interface LiveToastProps {
  live: FormattedLive | null;
  onClose: () => void;
  onSnoozeUntilTomorrow: () => void;
  onDismissForever: () => void;
}

interface LiveToastLayoutProps {
  children: ReactNode;
  onClose: () => void;
  title: ReactNode;
}

interface TimingBadgeProps {
  children: ReactNode;
}

const TOAST_ID = "live-banner";
const TOASTER_ID = "live-banner-toaster";
const TOASTER_Z_INDEX = 10000;

function LiveToastLayout({ children, onClose, title }: LiveToastLayoutProps) {
  return (
    <div className="flex w-134 flex-col gap-4 rounded-lg bg-amber-300 px-6 py-4 text-amber-950 shadow-lg ring-1 ring-black/5">
      <div className="flex items-center gap-2 pr-6">
        <div className="self-start rounded-md bg-amber-950/10 p-1.5">
          <Tv size={18} />
        </div>
        <p className="truncate text-xl font-medium whitespace-nowrap">
          {title}
        </p>
      </div>

      {children}

      <X
        className="absolute top-1.5 right-1.5 cursor-pointer text-amber-950"
        onClick={onClose}
        size={18}
      />
    </div>
  );
}

function TimingBadge({ children }: TimingBadgeProps) {
  return (
    <div className="flex shrink items-center gap-2 rounded bg-amber-100 px-2 py-1 text-sm">
      <CalendarClock size={18} />
      <p>{children}</p>
    </div>
  );
}

function MultipleOngoingLiveContent({ lives }: { lives: FormattedLiveItem[] }) {
  return (
    <div className="flex flex-col gap-2">
      {lives.map((liveItem) => (
        <div
          key={liveItem.mediaUuid}
          className="flex items-center justify-between gap-3 rounded px-2 py-1 text-sm"
        >
          <Link
            className="font-medium"
            to={`${LIVE_CONTENTS_ROUTE}/${liveItem.mediaUuid}`}
          >
            {liveItem.label}
          </Link>
          <TimingBadge>{liveItem.timingLabel}</TimingBadge>
        </div>
      ))}
    </div>
  );
}

function SingleLiveContent({
  live,
  onSnoozeUntilTomorrow,
  onDismissForever,
}: SingleLiveContentProps) {
  const { t } = useTranslation("editor-notif");

  return (
    <React.Fragment>
      {live.projectLabel ? (
        <p className="text-sm">{live.projectLabel}</p>
      ) : null}
      <div className="flex w-full items-center justify-between gap-2">
        <TimingBadge>{live.timingLabel}</TimingBadge>

        <ButtonGroup>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label="More Options"
                className="border-amber-950/20 bg-white text-amber-950 hover:bg-amber-50 hover:text-amber-950 aria-expanded:bg-amber-50 aria-expanded:text-amber-950 dark:border-amber-950/20 dark:bg-white dark:hover:bg-amber-50"
                variant="outline"
              >
                <EyeOff size={18} /> {t("live_banner_close")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-40"
              style={{ zIndex: TOASTER_Z_INDEX + 1 }}
            >
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={onSnoozeUntilTomorrow}>
                  {t("live_banner_snooze_until_tomorrow")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDismissForever}>
                  {t("live_banner_dismiss_forever")}
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </ButtonGroup>
      </div>
    </React.Fragment>
  );
}

function LiveToast({
  live,
  onClose,
  onSnoozeUntilTomorrow,
  onDismissForever,
}: LiveToastProps) {
  if (!live) return null;

  return (
    <LiveToastLayout onClose={onClose} title={live.title}>
      {live.type === "multiple-ongoing" ? (
        <MultipleOngoingLiveContent lives={live.lives} />
      ) : (
        <SingleLiveContent
          live={live}
          onDismissForever={onDismissForever}
          onSnoozeUntilTomorrow={onSnoozeUntilTomorrow}
        />
      )}
    </LiveToastLayout>
  );
}

export default function LiveBanner() {
  const [manualDismiss, setManualDismiss] = useState(false);

  const { isSnoozed, snoozeUntilTomorrow, dismissForever } =
    useLiveBannerSnooze();

  const { t } = useTranslation("editor-notif");
  const isLoggedIn = useSelector(
    (state: AppState) => state.loginReducer.isLoggedIn,
  );

  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );

  const getLivesStatsQuery = EncoderAPI.generateQuery({
    fn: EncoderAPI.getLivesStats,
    params: [],
    options: {
      refetchInterval: manualDismiss ? false : 30000,
      retryDelay: 15000,
      enabled: isLoggedIn,
    },
  });

  const { data: onGoingLives, isLoading } = useQuery(getLivesStatsQuery);

  const getProjects = useCallback(
    (live: LiveStats) => {
      if (!projects.data?.length || !live?.storefront_project_uuid?.length)
        return "";

      return live.storefront_project_uuid
        .map((uuid) => projects.data.find((p) => p.uuid === uuid)?.name)
        .filter(Boolean)
        .join(", ");
    },
    [projects.data],
  );

  const formatLive = useCallback(
    (
      status: "scheduled" | "ongoing",
      data: LiveStats | LiveStats[],
    ): FormattedLive => {
      const colon = t("content:colon");
      const formatTime = (date?: string, fmt?: string) =>
        moment.utc(date).local().format(fmt);

      if (Array.isArray(data)) {
        return {
          type: "multiple-ongoing",
          lives: data.map((live) => ({
            label: live.media_label || live.media_uuid,
            mediaUuid: live.media_uuid,
            timingLabel: `${t("will_end")} ${formatTime(live.live_end, TIME_FORMAT_SHORT)}`,
          })),
          title: `${t("ongoing_lives")}${colon} ${data.length}`,
        };
      }

      const projectNames = getProjects(data);
      const isScheduled = status === "scheduled";
      let prefixKey = isScheduled ? "next_live" : "ongoing_live";

      if (projectNames) {
        prefixKey = isScheduled ? "next_live_on" : "ongoing_live_on";
      }

      const title = (
        <React.Fragment>
          {t(isScheduled ? "next_live" : "ongoing_live")}
          {colon}{" "}
          <Link
            className="font-medium"
            to={`${LIVE_CONTENTS_ROUTE}/${data.media_uuid}`}
          >
            {data.media_label || data.media_uuid}
          </Link>
        </React.Fragment>
      );

      const timingLabel = isScheduled
        ? `${t("scheduled_for")}${colon} ${formatTime(data.live_start, "DD MMMM YYYY")} ${t("at")} ${formatTime(data.live_start, TIME_FORMAT_SHORT)}`
        : `${t("will_end")} ${formatTime(data.live_end, TIME_FORMAT_SHORT)}`;

      return {
        type: status,
        label: data.media_label || data.media_uuid,
        mediaUuid: data.media_uuid,
        projectLabel: projectNames ? `${t(prefixKey)} ${projectNames}` : "",
        timingLabel,
        title,
      };
    },
    [getProjects, t],
  );

  const sortedLive = useCallback(() => {
    if (!onGoingLives?.data) return null;

    // On extrait les éléments avec des filtres précis
    const ongoing = onGoingLives.data.filter(({ status }) =>
      LIVE_STATUSES.includes(status),
    );

    if (ongoing.length > 0) {
      if (ongoing.length === 1) {
        const [singleOngoingLive] = ongoing;

        if (singleOngoingLive) {
          return formatLive("ongoing", singleOngoingLive);
        }
      }

      return formatLive("ongoing", ongoing);
    }
    // Sinon, on cherche le prochain planifié le plus proche chronologiquement
    const [nextScheduled] = onGoingLives.data
      .filter(({ status }) => status === "scheduled")
      .sort((a, b) => moment(a.live_start).diff(moment(b.live_start)));

    if (nextScheduled) {
      return formatLive("scheduled", nextScheduled);
    }

    return null;
  }, [formatLive, onGoingLives]);

  const handleClose = useCallback(() => {
    setManualDismiss(true);
    toast.dismiss(TOAST_ID);
  }, []);

  useEffect(() => {
    if (
      projects.isFetching ||
      isLoading ||
      manualDismiss ||
      !onGoingLives?.data ||
      onGoingLives.data.length === 0
    ) {
      toast.dismiss(TOAST_ID);
    } else {
      const live = sortedLive();
      const currentMediaUuid =
        live?.type !== "multiple-ongoing" ? (live?.mediaUuid ?? null) : null;

      if (currentMediaUuid && isSnoozed(currentMediaUuid)) {
        toast.dismiss(TOAST_ID);
        return;
      }

      toast.custom(
        () => (
          <LiveToast
            live={live}
            onClose={handleClose}
            onDismissForever={() => {
              if (currentMediaUuid) {
                dismissForever(currentMediaUuid);
                toast.dismiss(TOAST_ID);
              }
            }}
            onSnoozeUntilTomorrow={() => {
              if (currentMediaUuid) {
                snoozeUntilTomorrow(currentMediaUuid);
                toast.dismiss(TOAST_ID);
              }
            }}
          />
        ),
        {
          duration: Infinity,
          richColors: true,
          id: TOAST_ID,
          toasterId: TOASTER_ID,
        },
      );
    }
  }, [
    isLoading,
    manualDismiss,
    projects.isFetching,
    onGoingLives,
    getProjects,
    sortedLive,
    isSnoozed,
    handleClose,
    dismissForever,
    snoozeUntilTomorrow,
  ]);

  return (
    <Toaster
      className="font-default"
      id={TOASTER_ID}
      position="top-center"
      style={{
        pointerEvents: "auto",
        width: 536,
        zIndex: TOASTER_Z_INDEX,
      }}
    />
  );
}
